import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  LabelList,
} from "recharts";
import { OTIFPerformance } from "../../../types/contractDashboard";
import styles from "./otifList.module.scss";

interface OTIFListProps {
  data: OTIFPerformance[];
}

const OTIFList: React.FC<OTIFListProps> = ({ data }) => {
  const { chartData, average } = useMemo(() => {
    const avgItem = data.find((item) => item.average_delivery_performance);
    const avg = avgItem
      ? parseFloat(avgItem.average_delivery_performance!.replace("%", ""))
      : 0;

    // Calculate average first if not provided
    const tempData = data.filter(
      (item) => item.supplier_name && item.delivery_performance !== undefined,
    );
    const calculatedAvg =
      avg > 0
        ? avg
        : tempData.length > 0
        ? parseFloat(
            (
              tempData.reduce(
                (sum, item) => sum + item.delivery_performance!,
                0,
              ) / tempData.length
            ).toFixed(2),
          )
        : 0;

    const supplierData = tempData.map((item) => ({
      name: item.supplier_name!,
      percentage: parseFloat(item.delivery_performance!.toFixed(2)),
      status: item.delivery_performance! >= calculatedAvg ? "above" : "below",
    }));

    return { chartData: supplierData, average: calculatedAvg };
  }, [data]);

  const getStatusColor = (status: string) => {
    return status === "above" ? "#10b981" : "#f59e0b"; // Green above avg, Orange below avg
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const percentage = payload[0].value;
      const status = payload[0].payload.status;

      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "10px 14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#111827",
              fontWeight: 600,
              mb: 0.5,
              fontSize: "13px",
            }}
          >
            {payload[0].payload.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: getStatusColor(status),
              fontWeight: 600,
              fontSize: "16px",
              mb: 0.5,
            }}
          >
            {percentage}%
          </Typography>
          {average && (
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontSize: "11px",
                display: "block",
              }}
            >
              {percentage >= average
                ? `${(percentage - average).toFixed(1)}% above avg`
                : `${(average - percentage).toFixed(1)}% below avg`}
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  // Custom label to show percentages
  const CustomLabel = (props: any) => {
    const { x, y, width, value, payload } = props;

    // Safely get the status from the payload
    const status = payload?.status || (value >= average ? "above" : "below");

    return (
      <g>
        <text
          x={x + width + 8}
          y={y + 14}
          fill={getStatusColor(status)}
          fontSize={12}
          fontWeight={600}
        >
          {value}%
        </text>
      </g>
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Typography variant="h6" className={styles.title}>
            On-Time-In-Full (OTIF)
          </Typography>
          <Typography variant="h6" sx={{ color: "#6b7280", fontSize: "16px", fontWeight: 600 }}>
            {average.toFixed(1)}% Avg
          </Typography>
        </Box>
        <Typography variant="body2" className={styles.subtitle}>
          Delivery performance by top suppliers.
        </Typography>
      </Box>

      <Box className={styles.chartContainer} sx={{ position: "relative" }}>

        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 50, left: 5, bottom: 5 }}
            barCategoryGap="10%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={{ stroke: "#d1d5db" }}
              tickLine={false}
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: "#374151", fontSize: 11, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.03)" }}
              wrapperStyle={{ zIndex: 1000 }}
            />
            <ReferenceLine
              x={average}
              stroke="#6b7280"
              strokeDasharray="5 5"
              strokeWidth={1.5}
            />
            <Bar dataKey="percentage" radius={[0, 6, 6, 0]} barSize={28}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getStatusColor(entry.status)}
                  opacity={0.9}
                />
              ))}
              <LabelList dataKey="percentage" content={<CustomLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default OTIFList;
